import React, { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, X, Trash2 } from "lucide-react";
import { REGION_OPTIONS, TEAM_OPTIONS } from "./proposals/schemas/summaryFormSchema";

interface RFPWithCounts {
  id: string;
  title: string;
  description: string;
  matter_name: string | null;
  region: string | null;
  team: string | null;
  status: string;
  submission_deadline: string;
  created_at: string;
  responded_count: number;
  unresponded_count: number;
  min_bid: number | null;
  min_decrement: number | null;
}

const ActiveProposals = () => {
  // Render the title in the outlet context
  if (useOutletContext() === "title-only") {
    return "Active Requests";
  }
  
  const [rfps, setRfps] = useState<RFPWithCounts[]>([]);
  const [filteredRfps, setFilteredRfps] = useState<RFPWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { toast } = useToast();
  const { user } = useAuth();

  // Filter states
  const [regionFilter, setRegionFilter] = useState<string>("all");
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [matterSearch, setMatterSearch] = useState<string>("");

  // Fetch RFPs function
  const fetchRfps = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Fetch RFPs owned by the current user with active statuses
      const { data: rfpData, error: rfpError } = await supabase
        .from("rfps")
        .select(`
          id,
          title,
          description,
          matter_name,
          region,
          team,
          status,
          submission_deadline,
          created_at,
          min_bid,
          min_decrement
        `)
        .eq("buyer_id", user.id)
        .in("status", ["OPEN_FOR_SUBMISSIONS", "AWAITING_AUCTION", "AUCTION_ACTIVE"])
        .order("created_at", { ascending: false });

      if (rfpError) throw rfpError;

      // For each RFP, get the invitation and proposal counts
      const rfpsWithCounts = await Promise.all(
        (rfpData || []).map(async (rfp) => {
          // Get invitations for this RFP
          const { data: invitations } = await supabase
            .from("supplier_invitations")
            .select("id")
            .eq("rfp_id", rfp.id);

          // Get proposals for this RFP
          const { data: proposals } = await supabase
            .from("proposals")
            .select("id")
            .eq("rfp_id", rfp.id);

          const totalInvites = invitations?.length || 0;
          const respondedCount = proposals?.length || 0;
          const unrespondedCount = Math.max(0, totalInvites - respondedCount);

          return {
            ...rfp,
            responded_count: respondedCount,
            unresponded_count: unrespondedCount,
          };
        })
      );

      setRfps(rfpsWithCounts);
      setFilteredRfps(rfpsWithCounts);
    } catch (error) {
      console.error("Error fetching RFPs:", error);
      toast({
        title: "Error loading requests",
        description: "There was a problem loading your requests. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and set up real-time subscription
  useEffect(() => {
    fetchRfps();

    // Set up real-time subscription
    const channel = supabase
      .channel('active-rfps-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rfps' }, () => {
        fetchRfps();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'proposals' }, () => {
        fetchRfps();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'supplier_invitations' }, () => {
        fetchRfps();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Apply filters
  useEffect(() => {
    let filtered = [...rfps];

    // Region filter
    if (regionFilter && regionFilter !== "all") {
      filtered = filtered.filter(rfp => rfp.region === regionFilter);
    }

    // Team filter
    if (teamFilter && teamFilter !== "all") {
      filtered = filtered.filter(rfp => rfp.team === teamFilter);
    }

    // Matter name search (case-insensitive partial match)
    if (matterSearch.trim()) {
      const searchLower = matterSearch.toLowerCase().trim();
      filtered = filtered.filter(rfp => 
        (rfp.matter_name?.toLowerCase().includes(searchLower)) ||
        (rfp.title?.toLowerCase().includes(searchLower))
      );
    }

    setFilteredRfps(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [rfps, regionFilter, teamFilter, matterSearch]);

  const clearFilters = () => {
    setRegionFilter("all");
    setTeamFilter("all");
    setMatterSearch("");
  };

  const handleDeleteRfp = async (rfpId: string) => {
    try {
      // Delete related records first (due to foreign key constraints)
      await supabase.from("bids").delete().eq("rfp_id", rfpId);
      await supabase.from("proposals").delete().eq("rfp_id", rfpId);
      await supabase.from("supplier_invitations").delete().eq("rfp_id", rfpId);
      await supabase.from("rfp_decisions").delete().eq("rfp_id", rfpId);
      
      // Finally delete the RFP
      const { error } = await supabase.from("rfps").delete().eq("id", rfpId);
      
      if (error) throw error;
      
      toast({
        title: "Request deleted",
        description: "The request has been successfully deleted.",
      });
      
      fetchRfps();
    } catch (error) {
      console.error("Error deleting RFP:", error);
      toast({
        title: "Error",
        description: "Failed to delete the request. Please try again.",
        variant: "destructive",
      });
    }
  };

  const hasActiveFilters = regionFilter !== "all" || teamFilter !== "all" || matterSearch.trim() !== "";

  // Get label for region/team
  const getRegionLabel = (value: string | null) => {
    if (!value) return "—";
    const option = REGION_OPTIONS.find(o => o.value === value);
    return option?.label || value;
  };

  const getTeamLabel = (value: string | null) => {
    if (!value) return "—";
    const option = TEAM_OPTIONS.find(o => o.value === value);
    return option?.label || value;
  };

  // Format status for display
  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "OPEN_FOR_SUBMISSIONS":
        return "default";
      case "AWAITING_AUCTION":
        return "secondary";
      case "AUCTION_ACTIVE":
        return "destructive";
      default:
        return "outline";
    }
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredRfps.length / itemsPerPage);
  const currentRfps = filteredRfps.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
        <p className="text-gray-600 mb-6">
          View and manage your active requests here.
        </p>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg z-50">
                <SelectItem value="all">All Regions</SelectItem>
                {REGION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg z-50">
                <SelectItem value="all">All Teams</SelectItem>
                {TEAM_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search matter name..."
                value={matterSearch}
                onChange={(e) => setMatterSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
              <X className="h-4 w-4" />
              Clear
            </Button>
          )}
        </div>
        
        {filteredRfps.length === 0 ? (
          <div className="p-8 border border-dashed border-gray-300 rounded-lg text-center">
            <p className="text-gray-500">
              {hasActiveFilters 
                ? "No requests match your filters" 
                : "Your active requests will appear here"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Matter Name</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Responded</TableHead>
                    <TableHead>Pending</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentRfps.map((rfp) => (
                    <TableRow key={rfp.id}>
                      <TableCell className="font-medium">
                        {rfp.matter_name || rfp.title}
                      </TableCell>
                      <TableCell>{getRegionLabel(rfp.region)}</TableCell>
                      <TableCell>{getTeamLabel(rfp.team)}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(rfp.status)}>
                          {formatStatus(rfp.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-green-600 font-medium">{rfp.responded_count}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-orange-600 font-medium">{rfp.unresponded_count}</span>
                      </TableCell>
                      <TableCell>
                        {new Date(rfp.submission_deadline).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/auctions/${rfp.id}/bids`}>
                              View Details
                            </Link>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Request</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{rfp.matter_name || rfp.title}"? This action cannot be undone and will remove all associated proposals and bids.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteRfp(rfp.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {Array.from({length: totalPages}).map((_, index) => (
                    <PaginationItem key={index}>
                      <PaginationLink
                        isActive={currentPage === index + 1}
                        onClick={() => setCurrentPage(index + 1)}
                        className="cursor-pointer"
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveProposals;
